'use strict';
import defaults from 'lodash/defaults';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import React, {Component, createElement} from 'react';
import {mapPropsStream} from 'recompose';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/pluck';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/withLatestFrom';
import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';


export const INIT = {target: {isValid: true}};

/**
 * @param {FormHOCOptions} o
 */
export const buildFormPropsStream = o => mapPropsStream(props$ => {
  /** @type {FormHOCOptions} */
  const options = {};
  defaults(options, o, {});
  const fields = options.fields;

  if (!isArray(fields)) {
    throw new Error('FormHOC expected to receive a valid options object');
  }

  const serializeModel = (modelState, model) => {
    const dirtyModel = {};

    fields.forEach(field => {
      const prop = field.property;
      if (modelState.isChanging[prop]) {
        setFieldValue(field, dirtyModel, prop, modelState.internalModel[prop], model);
      }
    });

    return isFunction(options.serializeModel)
      ? options.serializeModel(dirtyModel, model)
      : dirtyModel;
  };

  const states$ = [];
  const changeHandlers = {};
  const constraints = {};
  const canEdit = {};
  let canEditHasFunction = false;

  const isEditing$ = props$
    .map(props => props.isEditing)
    .distinctUntilChanged();

  const onReset$ = isEditing$
    .withLatestFrom(props$)
    .map(([isEditing, props]) => getInitialModelState(props, fields));

  fields.forEach(field => {
    const prop = field.property;
    constraints[prop] = field.constraints;

    canEdit[prop] = get(field, 'canEdit', true);
    if (isFunction(canEdit[prop])) {
      canEditHasFunction = true;
    }

    const onChange$ = new Subject();
    changeHandlers[prop] = event => onChange$.next(event);

    states$.push(props$
      .map(props => getFieldValue(field, props.model, prop))
      .distinctUntilChanged()
      .combineLatest(
        onChange$.startWith(INIT),
        (prevValue, event) => {
          const nextState = {prop};

          if (event === INIT) {
            // startWith case. Just forward the initial state.
            nextState.internalModel = prevValue;
            nextState.isChanging = false;

          } else {
            const nextValue = event.target.value;
            const isToggled = prevValue !== nextValue;
            nextState.internalModel = isToggled ? nextValue : prevValue;
            nextState.isChanging = isToggled;

          }

          if (field.useError) {
            nextState.hasError = !event.target.isValid;
          }

          return nextState;
        }
      ));
  });

  const canEdit$ = canEditHasFunction
    ? buildCanEdit$(props$, fields)
    : Observable.of(canEdit);

  const modelState$ = Observable
    .combineLatest(
      ...states$,
      (...states) => {
        const nextState = {
          internalModel: {},
          isChanging: {},
          hasError: {},
        };

        states.forEach(state => {
          nextState.internalModel[state.prop] = state.internalModel;
          nextState.isChanging[state.prop] = state.isChanging;
          nextState.hasError[state.prop] = state.hasError;
        });

        nextState.isDirty = getIsDirty(nextState.isChanging);

        return nextState;
      }
    )
    /* Skip the initialization */
    .skip(1);

  return props$
    .combineLatest(
      canEdit$,
      Observable
        .merge(
          modelState$,
          onReset$,
        ),
      (props, canEdit, modelState) => ({
        ...props,
        ...modelState,
        constraints,
        canEdit,
        onChange: changeHandlers,

        onSave: event => {
          if (event) {
            event.stopPropagation();
            event.preventDefault();
          }

          if (modelState.isDirty) {
            if (isFunction(props.onSave)) {
              props.onSave(serializeModel(modelState, props.model));
            }
          } else {
            if (isFunction(props.onCancel)) {
              props.onCancel();
            }
          }
        },
      })
    );
});

function getIsDirty(isChanging) {
  return Object
    .values(isChanging)
    .reduce((acc, isChanging) => isChanging || acc, false);
}

function buildCanEdit$(props$, fields) {
  return props$
    .pluck('model')
    .distinctUntilChanged()
    .withLatestFrom(props$)
    .map(([model, props]) => {
      const result = {};
      fields.forEach(field => {
        const prop = field.property;
        result[prop] = get(field, 'canEdit', true);
        if (isFunction(result[prop])) {
          result[prop] = result[prop](model, props);
        }
      });

      return result;
    });
}

function getInitialModelState(props, fields) {
  const nextState = {
    internalModel: {},
    isChanging: {},
    hasError: {},
  };

  fields.forEach(field => {
    const prop = field.property;

    nextState.internalModel[prop] = getFieldValue(field, props.model, prop);
    nextState.isChanging[prop] = false;
    nextState.hasError[prop] = false;
  });

  nextState.isDirty = getIsDirty(nextState.isChanging);

  return nextState;
}

function getFieldValue(field, model, prop) {
  if (isFunction(field.getValue)) {
    return field.getValue(model, prop);
  } else {
    return get(model, prop, null);
  }
}

function setFieldValue(field, dirtyModel, prop, value, model) {
  if (isFunction(field.setValue)) {
    return field.setValue(dirtyModel, prop, value, model);
  } else {
    dirtyModel[prop] = value;
  }
}

export const FormEventsHOC = WrappedComponent => {
  return class ComponentFromStream extends Component {
    componentDidMount() {
      if (this.props.triggerSave$ && isFunction(this.props.triggerSave$.subscribe)) {
        this.configureSave();
      }
    }

    componentDidUpdate(prevProps) {
      if (prevProps.isDirty !== this.props.isDirty && isFunction(this.props.onChangeIsDirty)) {
        this.props.onChangeIsDirty(this.props.isDirty);
      }
      if (prevProps.isEditing !== this.props.isEditing) {
        if (this.props.isEditing && isFunction(this.props.onEnterEdit)) {
          this.props.onEnterEdit();

        } else if (!this.props.isEditing && isFunction(this.props.onExitEdit)) {
          this.props.onExitEdit();

        }
      }
      if (!this.onSaveSubscription && this.props.triggerSave$ && isFunction(this.props.triggerSave$.subscribe)) {
        this.configureSave();
      }
    }

    componentWillUnmount() {
      this.onSaveSubscription && this.onSaveSubscription.unsubscribe();
    }

    configureSave() {
      this.onSaveSubscription = this.props.triggerSave$
        .subscribe(() => {
          if (isFunction(this.props.onSave)) {
            this.props.onSave(null);
          }
        });
    }

    render() {
      return createElement(WrappedComponent, this.props);
    }
  };
};

/**
 * Use when you do not need to compose the props stream.
 * @param {Component|Function} WrappedComponent
 * @param {FormHOCOptions} options
 */
export function buildFormHOC(WrappedComponent, options) {
  const formProps$ = buildFormPropsStream(options);

  return formProps$(FormEventsHOC(WrappedComponent));
}


/**
 * @typedef {Object} FormHOCOptions
 * @property {Array<FormHOCField>} fields
 * @property [modelPropType=PropTypes.object]
 * @property {Function} [serializeModel]
 */

/**
 * @typedef {Object} FormHOCField
 * @property {String} property
 * @property {Boolean|Function} canEdit Fn takes params: model, internalModel, props and must return Boolean
 * @property {Boolean} useError
 * @property {INPUT_TYPES} type
 * @property {Object} constraints
 * @property {Function} [getValue]
 * @property {Function} [setValue]
 */



// WEBPACK FOOTER //
// ./src/js/components/forms/Form/index.js